import { useEffect, useState, useCallback } from 'react';
import { useUser } from '../auth/auth.service';

// Storage key for anonymous user ID
const ANONYMOUS_USER_ID_KEY = 'optimizely_anonymous_user_id';

/**
 * Generate a random ID for anonymous users
 */
const generateAnonymousId = (): string => {
  return `anonymous-${Math.random().toString(36).substring(2, 15)}-${Date.now().toString(36)}`;
};

/**
 * Custom hook to get the current user ID for Optimizely
 * - Uses authenticated user ID when available
 * - Falls back to a persistent anonymous ID for non-authenticated users
 * - Ensures consistent user identification across page loads
 * 
 * @returns {string} The user ID to use with Optimizely
 */
export const useOptimizelyUserId = (): string => {
  const [userId, setUserId] = useState<string>('');
  const { data: userData, isSuccess, isError } = useUser(true);

  // Get or create an anonymous user ID
  const getAnonymousUserId = useCallback((): string => {
    try {
      // Try to get from sessionStorage first
      let sessionUserId = sessionStorage.getItem(ANONYMOUS_USER_ID_KEY);
      
      // If not in session, try localStorage for persistence across sessions
      if (!sessionUserId) {
        sessionUserId = localStorage.getItem(ANONYMOUS_USER_ID_KEY);
        
        // If not found anywhere, create a new one
        if (!sessionUserId) {
          sessionUserId = generateAnonymousId();
          
          // Store in both storages for redundancy
          localStorage.setItem(ANONYMOUS_USER_ID_KEY, sessionUserId);
        }
        
        // Always update sessionStorage for faster access
        sessionStorage.setItem(ANONYMOUS_USER_ID_KEY, sessionUserId);
      }
      
      return sessionUserId;
    } catch (error) {
      // In case of errors with storage (e.g., private browsing mode)
      console.error('Error accessing storage for Optimizely user ID', error);
      return generateAnonymousId();
    }
  }, []);

  useEffect(() => {
    // If we have user data, use the user ID
    if (isSuccess && userData?.data) {
      // Convert the numeric ID to string
      const id = userData.data.id.toString();
      // Use loginId as fallback
      setUserId(id || userData.data.loginId || 'unknown-user');
    } else if (isError || !userData) {
      // If error or no user data, use anonymous ID
      setUserId(getAnonymousUserId());
    }
  }, [isSuccess, isError, userData, getAnonymousUserId]);

  return userId;
};
